import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

export interface ApiResponse<T = any> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
}

export interface PaginatedResponse<T> extends ApiResponse<T[]> {
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

export abstract class BaseApiService {
  protected axiosInstance: AxiosInstance;
  protected basePath: string;

  constructor(basePath: string) {
    this.basePath = basePath;
    this.axiosInstance = axios.create({
      baseURL: API_BASE,
      timeout: 10000,
      withCredentials: false,
      headers: { "Content-Type": "application/json" },
    });

    this.axiosInstance.interceptors.request.use((config) => {
      const token = this.getAuthToken();
      if (token) {
        config.headers = config.headers ?? {};
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.axiosInstance.interceptors.response.use(
      (response) => response.data,
      (error) => {
        const message = error?.response?.data?.message || error?.message || 'Request failed';
        throw new Error(message);
      }
    );
  }

  private getAuthToken(): string {
    return (
      localStorage.getItem("authToken") ||
      localStorage.getItem("businessToken") ||
      localStorage.getItem("auth_token") ||
      ""
    );
  }

  protected async get<T>(endpoint: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.axiosInstance.get(`${this.basePath}${endpoint}`, config);
  }

  protected async post<T>(endpoint: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.axiosInstance.post(`${this.basePath}${endpoint}`, data, config);
  }

  protected async put<T>(endpoint: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.axiosInstance.put(`${this.basePath}${endpoint}`, data, config);
  }

  protected async patch<T>(endpoint: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.axiosInstance.patch(`${this.basePath}${endpoint}`, data, config);
  }

  protected async delete<T>(endpoint: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.axiosInstance.delete(`${this.basePath}${endpoint}`, config);
  }

  getConfig() {
    return {
      baseURL: API_BASE,
      environment: process.env.NODE_ENV || 'development'
    };
  }
}